import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { ProgressStatus } from "../api/types";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export default function ProgressModal(_ref) {
  let {
    isOpen,
    heading = "Processing...",
    subheading = "Please hold while your operation is in progress.",
    children,
    ...props
  } = _ref;
  return /*#__PURE__*/_jsx(Transition.Root, {
    show: isOpen,
    as: Fragment,
    children: /*#__PURE__*/_jsxs(Dialog, {
      as: "div",
      "data-testid": "progress-modal",
      className: "relative z-10",
      onClose: () => {
        /* Don't close the dialog when clicking the backdrop */
      },
      children: [/*#__PURE__*/_jsx(Transition.Child, {
        as: Fragment,
        enter: "ease-out duration-300",
        enterFrom: "opacity-0",
        enterTo: "opacity-100",
        leave: "ease-in duration-200",
        leaveFrom: "opacity-100",
        leaveTo: "opacity-0",
        children: /*#__PURE__*/_jsx("div", {
          className: "fixed inset-0 bg-grey-400 bg-opacity-75 transition-opacity"
        })
      }), /*#__PURE__*/_jsx("div", {
        className: "fixed z-10 inset-0 overflow-y-auto",
        children: /*#__PURE__*/_jsx("div", {
          className: "flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0",
          children: /*#__PURE__*/_jsx(Transition.Child, {
            as: Fragment,
            enter: "ease-out duration-300",
            enterFrom: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95",
            enterTo: "opacity-100 translate-y-0 sm:scale-100",
            leave: "ease-in duration-200",
            leaveFrom: "opacity-100 translate-y-0 sm:scale-100",
            leaveTo: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95",
            children: /*#__PURE__*/_jsxs(Dialog.Panel, {
              className: "relative bg-white px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full sm:p-6",
              children: [/*#__PURE__*/_jsx("div", {
                className: "sm:flex sm:items-start",
                children: /*#__PURE__*/_jsxs("div", {
                  className: "mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left",
                  children: [/*#__PURE__*/_jsx(Dialog.Title, {
                    as: "h3",
                    className: "text-base leading-6 font-semibold text-grey-500",
                    children: heading
                  }), /*#__PURE__*/_jsx("div", {
                    className: "mt-2",
                    children: /*#__PURE__*/_jsx("p", {
                      className: "text-sm text-grey-400",
                      children: subheading
                    })
                  })]
                })
              }), /*#__PURE__*/_jsx("nav", {
                "aria-label": "Progress",
                className: "ml-4 mt-11 mb-6",
                children: /*#__PURE__*/_jsx("ol", {
                  className: "overflow-hidden",
                  children: props.steps.map((step, stepIdx) => /*#__PURE__*/_jsx("li", {
                    className: `relative ${stepIdx !== props.steps.length - 1 && "pb-10"}`,
                    "data-testid": `${step.name}-${step.status}`,
                    children: step.status === ProgressStatus.IS_SUCCESS ? /*#__PURE__*/_jsx(ModalStep, {
                      step: step,
                      icon: /*#__PURE__*/_jsx("span", {
                        className: "relative z-10 w-8 h-8 flex items-center justify-center bg-teal-500 rounded-full",
                        "data-testid": `${step.name}-complete-icon`,
                        children: /*#__PURE__*/_jsx(CheckIcon, {
                          className: "w-5 h-5 text-white",
                          "aria-hidden": "true"
                        })
                      }),
                      line: /*#__PURE__*/_jsx("div", {
                        className: "-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-teal-500",
                        "aria-hidden": "true"
                      }),
                      nameColor: "text-grey-500",
                      descriptionColor: "text-grey-500",
                      isLastStep: stepIdx === props.steps.length - 1
                    }) : step.status === ProgressStatus.IN_PROGRESS ? /*#__PURE__*/_jsx(ModalStep, {
                      step: step,
                      icon: /*#__PURE__*/_jsx("span", {
                        className: "relative z-10 w-8 h-8 flex items-center justify-center bg-white border-2 border-violet-500 rounded-full",
                        children: /*#__PURE__*/_jsx("span", {
                          className: "h-2.5 w-2.5 bg-violet-500 rounded-full animate-pulse-scale",
                          "data-testid": `${step.name}-current-icon`
                        })
                      }),
                      line: /*#__PURE__*/_jsx("div", {
                        className: "-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-grey-200",
                        "aria-hidden": "true"
                      }),
                      nameColor: "text-violet-500",
                      isLastStep: stepIdx === props.steps.length - 1
                    }) : step.status === ProgressStatus.IS_ERROR ? /*#__PURE__*/_jsx(ModalStep, {
                      step: step,
                      icon: /*#__PURE__*/_jsx("span", {
                        className: "relative z-10 w-8 h-8 flex items-center justify-center border-2 bg-white border-pink-500 rounded-full",
                        children: /*#__PURE__*/_jsx(XMarkIcon, {
                          className: "w-5 h-5 text-pink-500",
                          "data-testid": `${step.name}-error-icon`
                        })
                      }),
                      line: /*#__PURE__*/_jsx("div", {
                        className: "-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-grey-300",
                        "aria-hidden": "true"
                      }),
                      isLastStep: stepIdx === props.steps.length - 1,
                      nameColor: "text-grey-500"
                    }) : step.status === ProgressStatus.NOT_STARTED ? /*#__PURE__*/_jsx(ModalStep, {
                      step: step,
                      icon: /*#__PURE__*/_jsx("span", {
                        className: "relative z-10 w-8 h-8 flex items-center justify-center bg-white border-2 rounded-full border-grey-400",
                        "data-testid": `${step.name}-upcoming-icon`
                      }),
                      line: /*#__PURE__*/_jsx("div", {
                        className: "-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-grey-300",
                        "aria-hidden": "true"
                      }),
                      isLastStep: stepIdx === props.steps.length - 1,
                      nameColor: "text-grey-400"
                    }) : /*#__PURE__*/_jsx(_Fragment, {})
                  }, stepIdx))
                })
              })]
            })
          })
        })
      }), /*#__PURE__*/_jsx("button", {
        className: "h-0 w-0 overflow-hidden"
      }), children]
    })
  });
}
function ModalStep(props) {
  var _props$descriptionCol;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [!props.isLastStep ? props.line : null, /*#__PURE__*/_jsxs("div", {
      className: "relative flex items-start group",
      "aria-current": !!props.isAriaCurrent,
      children: [/*#__PURE__*/_jsx("span", {
        className: "h-9 flex items-center",
        "aria-hidden": !!props.isAriaHidden,
        children: props.icon
      }), /*#__PURE__*/_jsxs("span", {
        className: "ml-4 min-w-0 flex flex-col",
        children: [/*#__PURE__*/_jsx("span", {
          className: `text-xs font-semibold tracking-wide uppercase ${props.nameColor}`,
          children: props.step.name
        }), /*#__PURE__*/_jsx("span", {
          className: `text-sm ${(_props$descriptionCol = props.descriptionColor) !== null && _props$descriptionCol !== void 0 ? _props$descriptionCol : "text-grey-400"}`,
          children: props.step.description
        })]
      })]
    })]
  });
}
export const errorModalDelayMs = 3000;